<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const faq = [
  {
    q: 'Apakah perubahan tarif pajak berlaku untuk transaksi yang sudah ada?',
    a: 'Tidak. Perubahan tarif pajak hanya berlaku untuk transaksi baru setelah Anda menyimpan pengaturan. Transaksi lama tetap menggunakan tarif pajak yang berlaku saat transaksi tersebut dibuat.',
  },
  {
    q: 'Toko saya tidak kena pajak (non-PKP). Harus isi berapa tarif pajaknya?',
    a: 'Isi 0 (nol). Dengan tarif pajak 0%, sistem tidak akan menambahkan pajak pada struk dan laporan penjualan Anda.',
  },
  {
    q: 'Apa bedanya WIB, WITA, dan WIT?',
    a: 'WIB (Waktu Indonesia Barat) untuk Jawa, Sumatra, Kalimantan Barat/Tengah. WITA (Waktu Indonesia Tengah) untuk Bali, Sulawesi, Kalimantan Selatan/Timur/Utara, NTB. WIT (Waktu Indonesia Timur) untuk Papua, Maluku. Pilih zona waktu sesuai lokasi toko Anda agar timestamp transaksi akurat.',
  },
  {
    q: 'Pesan footer struk saya tidak muncul di struk. Kenapa?',
    a: 'Pastikan pengaturan "Tampilkan Logo di Struk" dan koneksi printer thermal sudah benar. Pesan footer hanya muncul di struk yang dicetak via printer thermal — tidak muncul di preview layar kasir.',
  },
  {
    q: 'Berapa sebaiknya nilai Batas Stok Kritis?',
    a: 'Tidak ada patokan pasti — tergantung seberapa cepat Anda menghabiskan bahan dan seberapa sering Anda restock. Aturan praktisnya: isi dengan stok yang cukup untuk 2–3 hari operasional. Contoh: jika Anda pakai 500g kopi per hari dan restock tiap 3 hari, isi batas kritis = 1.500g.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink
            to="/userguide"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            User Guide
          </RouterLink>
          <VIcon
            icon="ri-arrow-right-s-line"
            size="16"
            class="text-medium-emphasis"
          />
          <RouterLink
            to="/userguide/sistem/pengaturan"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            Settings
          </RouterLink>
          <VIcon
            icon="ri-arrow-right-s-line"
            size="16"
            class="text-medium-emphasis"
          />
          <span class="text-body-2">Pengaturan Umum</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar
            color="success"
            variant="tonal"
            size="56"
            rounded="lg"
            class="me-3"
          >
            <VIcon
              icon="ri-equalizer-line"
              size="30"
            />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-0">
              Pengaturan Umum
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Konfigurasi pajak, zona waktu, struk, dan inventory toko Anda
            </p>
          </div>
        </div>
      </VCol>
    </VRow>

    <!-- Tujuan halaman -->
    <VRow>
      <VCol cols="12">
        <VAlert
          type="info"
          variant="tonal"
          icon="ri-flag-line"
        >
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda mengisi semua pengaturan dasar toko — pajak, zona waktu, pesan struk,
          dan batas stok kritis. Setelah selesai, sistem siap digunakan untuk transaksi harian.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Cara Buka Halaman -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-cursor-line"
              class="me-2"
              color="success"
            />
            Cara Membuka Halaman Settings
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Ada dua cara untuk membuka halaman Pengaturan:
            </p>
            <VList density="compact">
              <VListItem prepend-icon="ri-arrow-right-s-line">
                <VListItemTitle class="text-body-2">
                  Klik menu <strong>Settings</strong> di sidebar kiri
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-arrow-right-s-line">
                <VListItemTitle class="text-body-2">
                  Atau langsung buka URL:
                  <code class="text-caption">/settings/general</code>
                </VListItemTitle>
              </VListItem>
            </VList>
            <p class="text-body-2 mt-3 mb-0">
              Halaman settings memiliki 4 tab di sisi kiri: <strong>Umum, Pajak & Keuangan,
              Struk,</strong> dan <strong>Inventory</strong>. Klik tab untuk berpindah antar bagian.
              Setiap tab punya tombol <strong>"Simpan Pengaturan"</strong> tersendiri.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tab 1: Umum -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-store-2-line"
              class="me-2"
              color="primary"
            />
            Tab 1 — Umum: Zona Waktu & Mata Uang
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Tab ini berisi informasi toko (nama, email, telepon, alamat) yang ditampilkan
              sebagai referensi. Data ini <strong>tidak bisa diedit</strong> dari sini — diatur
              oleh admin sistem. Yang bisa Anda ubah adalah zona waktu.
            </p>

            <VTable>
              <thead>
                <tr>
                  <th style="min-width: 140px">
                    Pengaturan
                  </th>
                  <th style="min-width: 100px">
                    Wajib?
                  </th>
                  <th>Penjelasan & Contoh</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>Zona Waktu</strong>
                  </td>
                  <td>
                    <VChip
                      color="warning"
                      size="x-small"
                      variant="tonal"
                    >
                      Dianjurkan
                    </VChip>
                  </td>
                  <td class="text-body-2 py-3">
                    Pilih zona waktu sesuai lokasi toko Anda. Ini mempengaruhi timestamp
                    di semua transaksi dan laporan.
                    <br><span class="text-medium-emphasis">
                      Pilihan: <strong>WIB</strong> (Jakarta), <strong>WITA</strong> (Makassar),
                      <strong>WIT</strong> (Jayapura)
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Mata Uang</strong>
                  </td>
                  <td>
                    <VChip
                      color="default"
                      size="x-small"
                      variant="tonal"
                    >
                      Tidak perlu diubah
                    </VChip>
                  </td>
                  <td class="text-body-2 py-3">
                    Selalu <strong>IDR (Rupiah)</strong> — tidak bisa diubah untuk saat ini.
                    Semua nominal harga ditampilkan dalam Rupiah.
                  </td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tab 2: Pajak -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-percent-line"
              class="me-2"
              color="warning"
            />
            Tab 2 — Pajak & Keuangan: Tarif PPN
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Tarif pajak yang Anda isi di sini akan otomatis ditambahkan ke setiap transaksi
              di kasir. Pastikan sudah sesuai dengan status PKP toko Anda.
            </p>

            <VAlert
              type="warning"
              variant="tonal"
              class="mb-4"
            >
              <strong>Penting:</strong> Isi nilai persentase langsung.
              Contoh: untuk PPN 11%, isi <strong>11</strong> (bukan 0,11).
              Sistem akan mengkonversi otomatis.
            </VAlert>

            <VTable>
              <thead>
                <tr>
                  <th style="min-width: 140px">
                    Field
                  </th>
                  <th style="min-width: 80px">
                    Wajib?
                  </th>
                  <th>Penjelasan & Contoh</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>Tarif Pajak (%)</strong>
                  </td>
                  <td>
                    <VChip
                      color="success"
                      size="x-small"
                      variant="tonal"
                    >
                      ✅ Wajib diisi
                    </VChip>
                  </td>
                  <td class="text-body-2 py-3">
                    Persentase pajak yang dikenakan per transaksi.
                    <br>
                    <strong>Contoh nilai:</strong>
                    <ul class="mt-1">
                      <li><strong>11</strong> — untuk PPN 11% (standar Indonesia)</li>
                      <li><strong>0</strong> — jika toko Anda tidak kena pajak (non-PKP)</li>
                      <li><strong>10</strong> — tarif lama / kesepakatan khusus</li>
                    </ul>
                    <br>
                    <span class="text-medium-emphasis">
                      Contoh pengaruh: Harga kopi Rp 30.000 + PPN 11% = total
                      <strong>Rp 33.300</strong> di struk
                    </span>
                  </td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tab 3: Struk -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-receipt-line"
              class="me-2"
              color="info"
            />
            Tab 3 — Struk: Tampilan Nota Pelanggan
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Pengaturan ini menentukan tampilan struk/nota yang dicetak untuk pelanggan
              setelah transaksi. Bisa disesuaikan dengan karakter toko Anda.
            </p>

            <VTable>
              <thead>
                <tr>
                  <th style="min-width: 160px">
                    Pengaturan
                  </th>
                  <th style="min-width: 80px">
                    Wajib?
                  </th>
                  <th>Penjelasan & Contoh</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>Tampilkan Logo di Struk</strong>
                  </td>
                  <td>
                    <VChip
                      color="default"
                      size="x-small"
                      variant="tonal"
                    >
                      Opsional
                    </VChip>
                  </td>
                  <td class="text-body-2 py-3">
                    Aktifkan toggle ini jika ingin logo toko muncul di bagian atas struk.
                    Logo diambil dari data profil toko yang diatur oleh admin.
                    <br>
                    <span class="text-medium-emphasis">
                      Matikan jika printer thermal Anda lambat saat cetak gambar.
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Pesan Footer Struk</strong>
                  </td>
                  <td>
                    <VChip
                      color="default"
                      size="x-small"
                      variant="tonal"
                    >
                      Opsional
                    </VChip>
                  </td>
                  <td class="text-body-2 py-3">
                    Teks yang muncul di bagian paling bawah struk. Maksimal 200 karakter.
                    <br>
                    <strong>Contoh teks yang bagus:</strong>
                    <ul class="mt-1">
                      <li><em>"Terima kasih sudah berkunjung! Follow IG @KopiSenja 😊"</em></li>
                      <li><em>"Barang yang sudah dibeli tidak dapat dikembalikan"</em></li>
                      <li><em>"Promo member: diskon 10% setiap hari Senin!"</em></li>
                    </ul>
                    <br>
                    <span class="text-medium-emphasis">
                      Di halaman settings, ada preview mini struk untuk melihat hasilnya
                      sebelum disimpan.
                    </span>
                  </td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tab 4: Inventory -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-archive-line"
              class="me-2"
              color="error"
            />
            Tab 4 — Inventory: Pengaturan Stok
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Pengaturan ini menentukan bagaimana sistem menangani stok bahan baku
              saat transaksi dilakukan.
            </p>

            <VTable>
              <thead>
                <tr>
                  <th style="min-width: 180px">
                    Pengaturan
                  </th>
                  <th style="min-width: 80px">
                    Wajib?
                  </th>
                  <th>Penjelasan & Contoh</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>Izinkan Stok Negatif</strong>
                  </td>
                  <td>
                    <VChip
                      color="error"
                      size="x-small"
                      variant="tonal"
                    >
                      Hati-hati!
                    </VChip>
                  </td>
                  <td class="text-body-2 py-3">
                    Jika diaktifkan, kasir tetap bisa memproses transaksi meskipun stok
                    bahan baku sudah habis.
                    <br><br>
                    <span class="text-success font-weight-medium">Kapan diaktifkan?</span>
                    Saat Anda belum sempat update stok setelah restock, tapi toko sudah
                    harus beroperasi.
                    <br><br>
                    <span class="text-error font-weight-medium">⚠️ Risiko:</span>
                    Stok bisa jadi minus di laporan — data inventory tidak akurat.
                    <strong>Nonaktifkan segera setelah kondisi darurat selesai.</strong>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Batas Stok Kritis</strong>
                  </td>
                  <td>
                    <VChip
                      color="warning"
                      size="x-small"
                      variant="tonal"
                    >
                      Dianjurkan
                    </VChip>
                  </td>
                  <td class="text-body-2 py-3">
                    Jumlah stok minimum sebelum sistem memberi peringatan.
                    Satuannya mengikuti satuan masing-masing bahan baku.
                    <br><br>
                    <strong>Contoh pengisian:</strong>
                    <ul class="mt-1">
                      <li>Isi <strong>10</strong> — artinya peringatan muncul jika stok ≤ 10 unit</li>
                      <li>Cocok untuk bahan yang satuannya sudah besar (botol, sachet, kg)</li>
                    </ul>
                    <br>
                    <span class="text-medium-emphasis">
                      💡 Batas kritis per bahan baku bisa diatur individual di halaman
                      Bahan Baku — pengaturan di sini hanya nilai default global.
                    </span>
                  </td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Step-by-step -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="success" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-checkbox-circle-line"
              class="me-2"
            />
            Cara Menyimpan Pengaturan
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2">
              <li class="mb-2">
                Buka <strong>Settings</strong> dari sidebar kiri
              </li>
              <li class="mb-2">
                Klik tab yang ingin diubah (Umum / Pajak & Keuangan / Struk / Inventory)
              </li>
              <li class="mb-2">
                Ubah nilai sesuai kebutuhan toko Anda
              </li>
              <li class="mb-2">
                Klik tombol <strong>"Simpan Pengaturan"</strong> di bagian bawah halaman
              </li>
              <li class="mb-0">
                Muncul notifikasi hijau <em>"Pengaturan berhasil disimpan"</em> — selesai! ✅
              </li>
            </ol>

            <VAlert
              type="info"
              variant="tonal"
              class="mt-4 mb-0"
            >
              Anda tidak perlu simpan semua tab sekaligus. Ubah satu tab, simpan,
              lalu pindah ke tab berikutnya.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard variant="outlined">
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-lightbulb-line"
              class="me-2"
              color="warning"
            />
            💡 Tips Praktis
          </VCardTitle>
          <VCardText>
            <VList density="compact">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Setup pajak di awal.</strong> Tarif pajak mempengaruhi semua
                  transaksi — atur sebelum kasir mulai beroperasi.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Manfaatkan preview struk.</strong> Sebelum simpan pesan footer,
                  lihat dulu di preview mini di halaman settings — pastikan teks tidak
                  terpotong.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Stok kritis = 20–30% stok normal.</strong>
                  Jika biasanya punya stok 1 kg kopi, set batas kritis di 200–300 gram.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          ❓ Pertanyaan yang Sering Ditanyakan
        </h5>

        <VExpansionPanels variant="accordion">
          <VExpansionPanel
            v-for="(item, i) in faq"
            :key="i"
          >
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">
                {{ item.a }}
              </p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related pages -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          🔗 Halaman Terkait
        </h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Kategori', sub: 'Buat kategori untuk produk', icon: 'ri-price-tag-3-line', color: 'primary', to: '/userguide/produk-inventory/kategori' },
              { title: 'Satuan', sub: 'Buat satuan untuk bahan baku', icon: 'ri-scales-2-line', color: 'warning', to: '/userguide/produk-inventory/satuan' },
              { title: 'Bahan Baku', sub: 'Input bahan baku setelah settings selesai', icon: 'ri-archive-line', color: 'info', to: '/userguide/produk-inventory/bahan-baku' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink
              :to="link.to"
              class="text-decoration-none"
            >
              <VCard
                variant="outlined"
                hover
              >
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar
                    :color="link.color"
                    variant="tonal"
                    size="40"
                    rounded
                  >
                    <VIcon
                      :icon="link.icon"
                      size="22"
                    />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">
                      {{ link.title }}
                    </p>
                    <p class="text-caption text-medium-emphasis mb-0">
                      {{ link.sub }}
                    </p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
